/**
 * Centralized Logging Service
 *
 * Provides structured logging with configurable log levels and context.
 * Integrates with monitoring services (Firebase) for production logging.
 */

export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
    NONE = 4,
}

export interface LogContext {
    userId?: string
    route?: string
    action?: string
    snapshotId?: string | null
    metadata?: Record<string, unknown>
}

export interface LogEntry {
    level: LogLevel
    message: string
    timestamp: string
    context?: LogContext
    error?: Error
}

export interface LoggerConfig {
    minLevel: LogLevel
    enableConsole: boolean
    enableRemote: boolean
    environment: 'development' | 'staging' | 'production'
    remoteLogger?: (entry: LogEntry) => Promise<void>
}

/**
 * Logger class with configurable log levels and structured logging
 */
export class Logger {
    private config: LoggerConfig
    private static instance: Logger | null = null

    constructor(config: LoggerConfig) {
        this.config = config
    }

    /**
     * Get or create singleton Logger instance
     */
    static getInstance(config?: LoggerConfig): Logger {
        if (!Logger.instance) {
            if (!config) {
                throw new Error(
                    'Logger must be initialized with config on first call'
                )
            }
            Logger.instance = new Logger(config)
        }
        return Logger.instance
    }

    /**
     * Reset singleton instance (useful for testing)
     */
    static resetInstance(): void {
        Logger.instance = null
        // Also reset the default logger
        resetDefaultLogger()
    }

    /**
     * Update logger configuration
     */
    updateConfig(config: Partial<LoggerConfig>): void {
        this.config = { ...this.config, ...config }
    }

    /**
     * Log a debug message
     */
    debug(message: string, context?: LogContext): void {
        this.log(LogLevel.DEBUG, message, context)
    }

    /**
     * Log an info message
     */
    info(message: string, context?: LogContext): void {
        this.log(LogLevel.INFO, message, context)
    }

    /**
     * Log a warning message
     */
    warn(message: string, context?: LogContext): void {
        this.log(LogLevel.WARN, message, context)
    }

    /**
     * Log an error message
     */
    error(message: string, error?: Error, context?: LogContext): void {
        this.log(LogLevel.ERROR, message, context, error)
    }

    /**
     * Internal log method
     */
    private log(
        level: LogLevel,
        message: string,
        context?: LogContext,
        error?: Error
    ): void {
        // Check if log level meets minimum threshold
        if (level < this.config.minLevel) {
            return
        }

        const entry: LogEntry = {
            level,
            message,
            timestamp: new Date().toISOString(),
            context,
            error,
        }

        // Log to console if enabled
        if (this.config.enableConsole) {
            this.logToConsole(entry)
        }

        // Log to remote service if enabled
        if (this.config.enableRemote && this.config.remoteLogger) {
            this.logToRemote(entry)
        }
    }

    /**
     * Log to console with appropriate method
     */
    private logToConsole(entry: LogEntry): void {
        const levelName = LogLevel[entry.level]
        const prefix = `[${entry.timestamp}] [${levelName}]`
        const contextStr = entry.context
            ? ` ${JSON.stringify(entry.context)}`
            : ''
        const fullMessage = `${prefix} ${entry.message}${contextStr}`

        switch (entry.level) {
            case LogLevel.DEBUG:
                // eslint-disable-next-line no-console -- this is the logger's console sink
                console.debug(fullMessage, entry.error)
                break
            case LogLevel.INFO:
                // eslint-disable-next-line no-console -- this is the logger's console sink
                console.info(fullMessage)
                break
            case LogLevel.WARN:
                console.warn(fullMessage)
                break
            case LogLevel.ERROR:
                console.error(fullMessage, entry.error)
                break
        }
    }

    /**
     * Log to remote monitoring service
     */
    private logToRemote(entry: LogEntry): void {
        if (!this.config.remoteLogger) {
            return
        }

        // Fire and forget - don't block on remote logging
        this.config.remoteLogger(entry).catch((err) => {
            // Only log remote logging failures to console in development
            if (this.config.environment === 'development') {
                console.warn('Failed to log to remote service:', err)
            }
        })
    }

    /**
     * Get current log level
     */
    getLogLevel(): LogLevel {
        return this.config.minLevel
    }

    /**
     * Set log level
     */
    setLogLevel(level: LogLevel): void {
        this.config.minLevel = level
    }
}

/**
 * Create logger configuration based on environment
 */
export function createLoggerConfig(
    environment: 'development' | 'staging' | 'production',
    remoteLogger?: (entry: LogEntry) => Promise<void>
): LoggerConfig {
    const configs: Record<string, LoggerConfig> = {
        development: {
            minLevel: LogLevel.DEBUG,
            enableConsole: true,
            enableRemote: false,
            environment: 'development',
            remoteLogger,
        },
        staging: {
            minLevel: LogLevel.INFO,
            enableConsole: true,
            enableRemote: true,
            environment: 'staging',
            remoteLogger,
        },
        production: {
            minLevel: LogLevel.WARN,
            enableConsole: false,
            enableRemote: true,
            environment: 'production',
            remoteLogger,
        },
    }

    return configs[environment]
}

/**
 * Default logger instance (must be initialized before use)
 */
let defaultLogger: Logger | null = null

/**
 * Reset default logger (for testing)
 */
export function resetDefaultLogger(): void {
    defaultLogger = null
}

/**
 * Initialize the default logger
 */
export function initializeLogger(config: LoggerConfig): Logger {
    defaultLogger = Logger.getInstance(config)
    return defaultLogger
}

/**
 * Get the default logger instance
 */
export function getLogger(): Logger {
    if (!defaultLogger) {
        throw new Error(
            'Logger not initialized. Call initializeLogger() first.'
        )
    }
    return defaultLogger
}

/**
 * Convenience functions for logging
 */
export const logger = {
    debug: (message: string, context?: LogContext) =>
        getLogger().debug(message, context),
    info: (message: string, context?: LogContext) =>
        getLogger().info(message, context),
    warn: (message: string, context?: LogContext) =>
        getLogger().warn(message, context),
    error: (message: string, error?: Error, context?: LogContext) =>
        getLogger().error(message, error, context),
}
